/**
 * SettingsModal — модалка настроек профиля.
 *
 *   🔊 Звуки UI — тумблер (persist localStorage)
 *   🌐 Язык — выбор RU / EN
 *
 * Haptic всегда включён — по решению админа, не выводится в UI.
 * Выход тоже не нужен — авторизация в Telegram Mini App автоматическая.
 */
import { motion, AnimatePresence } from 'motion/react';
import { X, Volume2, VolumeX, Globe, Music, Music2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import { useUserSettings, type UILanguage } from '@/hooks/useUserSettings';

interface Props {
  open: boolean;
  onClose: () => void;
}

const LANGUAGES: Array<{ id: UILanguage; labelKey: string }> = [
  { id: 'ru', labelKey: 'settings.language.ru' },
  { id: 'en', labelKey: 'settings.language.en' },
];

export function SettingsModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const sounds = useUISounds();
  const [settings, setSettings] = useUserSettings();

  const toggleSounds = () => {
    const next = !settings.soundsEnabled;
    haptic.selection();
    // Звук включения при ВКЛ (когда уже будет слышен); выкл — тишина.
    if (next) sounds.bellOpen();
    setSettings({ soundsEnabled: next });
  };

  const toggleLoaderSound = () => {
    const next = !settings.loaderSoundEnabled;
    haptic.selection();
    if (next && settings.soundsEnabled) sounds.bellOpen();
    setSettings({ loaderSoundEnabled: next });
  };

  const pickLanguage = (id: UILanguage) => {
    haptic.light();
    sounds.tap();
    setSettings({ language: id });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center px-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-md" />
          <motion.div
            className="relative w-full max-w-sm rounded-[28px] overflow-hidden"
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
              border: '1px solid var(--glass-stroke-strong)',
              boxShadow: 'var(--glass-shadow)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
              <h2 className="font-display text-[16px] font-extrabold text-[var(--text-primary)] tracking-tight">
                {t('settings.title')}
              </h2>
              <button
                type="button"
                aria-label={t('settings.closeAria')}
                onClick={() => { haptic.light(); sounds.bellClose(); onClose(); }}
                className="h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] transition"
                style={{ background: 'var(--glass-fill)' }}
              >
                <X size={14} strokeWidth={2.4} />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-4">
              {/* Звуки */}
              <SectionRow
                icon={settings.soundsEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                title={t('settings.sounds.title')}
                subtitle={t('settings.sounds.subtitle')}
              >
                <ToggleSwitch checked={settings.soundsEnabled} onChange={toggleSounds} />
              </SectionRow>

              {/* Звук загрузочного экрана */}
              <SectionRow
                icon={settings.loaderSoundEnabled ? <Music size={16} /> : <Music2 size={16} />}
                title={t('settings.loaderSound.title')}
                subtitle={t('settings.loaderSound.subtitle')}
              >
                <ToggleSwitch checked={settings.loaderSoundEnabled} onChange={toggleLoaderSound} />
              </SectionRow>

              {/* Язык */}
              <SectionRow
                icon={<Globe size={16} />}
                title={t('settings.language.title')}
                subtitle={t('settings.language.subtitle')}
              >
                <div className="flex flex-col gap-1.5 mt-2 w-full">
                  {LANGUAGES.map((lang) => {
                    const active = settings.language === lang.id;
                    return (
                      <button
                        key={lang.id}
                        type="button"
                        onClick={() => pickLanguage(lang.id)}
                        className="flex items-center justify-between text-[12px] rounded-xl px-3 py-2 transition active:scale-[0.98]"
                        style={{
                          background: active
                            ? 'color-mix(in oklab, var(--accent-primary) 18%, transparent)'
                            : 'var(--glass-fill)',
                          border: `1px solid ${active
                            ? 'color-mix(in oklab, var(--accent-primary) 40%, transparent)'
                            : 'var(--glass-stroke)'}`,
                          color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        }}
                      >
                        <span className="font-mono">{t(lang.labelKey)}</span>
                        {active ? (
                          <span className="text-[9px] uppercase tracking-[0.12em]">
                            {t('settings.language.selected')}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </SectionRow>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SectionRow({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="rounded-2xl p-3.5"
      style={{
        background: 'var(--glass-fill)',
        border: '1px solid var(--glass-stroke)',
      }}
    >
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-[var(--text-secondary)]"
          style={{ background: 'var(--glass-fill-strong)' }}>
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-display text-[13px] font-bold text-[var(--text-primary)]">
            {title}
          </p>
          {subtitle && (
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{subtitle}</p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className="relative inline-flex items-center rounded-full transition float-right mt-1"
      style={{
        width: 46,
        height: 26,
        background: checked
          ? 'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 70%, var(--accent-secondary)))'
          : 'var(--glass-fill-strong)',
        border: `1px solid ${checked ? 'color-mix(in oklab, var(--accent-primary) 40%, transparent)' : 'var(--glass-stroke)'}`,
      }}
    >
      <span
        className="absolute top-[2px] rounded-full"
        style={{
          width: 20,
          height: 20,
          left: checked ? 22 : 2,
          background: checked ? '#0a0a0f' : 'var(--text-secondary)',
          transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
        }}
      />
    </button>
  );
}
